import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface SiteSetting {
  id: string;
  key: string;
  value: any;
  description: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface BetaDiscount {
  enabled: boolean;
  percentage: number;
}

export const useSiteSettings = () => {
  return useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .order('key');

      if (error) {
        console.error('Error fetching site settings:', error);
        throw error;
      }

      return data as SiteSetting[];
    },
    staleTime: 60000, // 1 minute
  });
};

export const useBetaDiscount = () => {
  return useQuery({
    queryKey: ["site-settings", "global_beta_discount"],
    queryFn: async () => {
      console.log("Starting beta discount fetch...");
      
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "global_beta_discount")
        .maybeSingle();

      console.log("Beta discount query result - data:", data, "error:", error);

      if (error) {
        console.error("Error fetching beta discount:", error);
        return { enabled: false, percentage: 0 };
      }
      
      if (!data) {
        console.log("No beta discount data found, returning default");
        return { enabled: false, percentage: 0 };
      }
      
      console.log("Raw value from database:", data.value);
      
      const value = data.value as any;
      const discount = {
        enabled: value?.enabled || false,
        percentage: value?.percentage || 0
      } as BetaDiscount;
      
      console.log("Beta discount fetched successfully:", discount);
      return discount;
    },
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
};

export const applyBetaDiscount = (price: number, betaDiscount?: BetaDiscount | null) => {
  if (!betaDiscount?.enabled || !betaDiscount?.percentage || betaDiscount.percentage <= 0) {
    return price;
  }
  return Math.round(price * (1 - betaDiscount.percentage / 100));
};

export const useUpdateSiteSetting = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('site_settings')
        .update({ 
          value: value,
          updated_by: user?.id 
        })
        .eq('key', key);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
      toast({
        title: "Settings updated",
        description: "Site settings have been saved successfully.",
      });
    },
    onError: (error) => {
      console.error('Error updating setting:', error);
      toast({
        title: "Error",
        description: "Failed to update settings. Please try again.",
        variant: "destructive",
      });
    },
  });
};